import React from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import useConstant from "use-constant";

// Formik
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";

// Custom
import DatePickerField from "../../components/FormikFields/DatePickerField";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { isValidDate, isBeforeDate } from "../../utils/datetime";

// Material UI
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Button, Grid } from "@mui/material";
import { GroupsFilter, LocationsFilter } from "../../components/FormikFields";
import PositionsFilter from "../../components/FormikFields/Filters/PositionsFilter";
import ExpandableFilters from "../../components/Filters/ExpandableFilters";
import StateSelector from "../../components/FormikFields/StateSelector";

const getValidationSchema = t => {
  return Yup.object().shape({
    hiredSince: Yup.mixed().test(
      "hiredSince",
      t("validationMessages.validDate"),
      function() {
        return (
          !this.parent.hiredSince ||
          (isValidDate(this.parent.hiredSince) &&
            !isBeforeDate(this.parent.hiredSince, "01/01/1900"))
        );
      }
    )
  });
};

const EmployeesFilters = ({ onSubmit }) => {
  const { t } = useTranslation();

  const initialValues = {
    searchName: "",
    hiredSince: null,
    group: "",
    location: "",
    state: "",
    position: ""
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false);
        onSubmit(values);
      }}
      validationSchema={getValidationSchema(t)}
    >
      {({ submitForm, handleReset }) => {
        const debouncedSubmit = useConstant(() => debounce(submitForm, 500));
        return (
          <Form>
            <ExpandableFilters
              staticContent={
                <Field
                  fullWidth
                  label={t("employeesFilters.searchName")}
                  variant="filled"
                  component={TextField}
                  name={t("searchName")}
                  onKeyUp={debouncedSubmit}
                  type="text"
                  disabled={false}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              }
              expandableContent={
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="hiredSince"
                      component={DatePickerField}
                      label={t("employeesFilters.hiredSince")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <GroupsFilter
                      fieldName="group"
                      filters={{ active: true, hasEmployees: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LocationsFilter fieldName="location" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PositionsFilter fieldName="position" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StateSelector
                      autocompleteProps={{ id: "state-selector" }}
                    />
                  </Grid>
                  <Grid
                    container
                    item
                    xs={12}
                    md={4}
                    sx={{ alignItems: "center", justifyContent: "flex-end" }}
                  >
                    <ButtonGroup>
                      <Button
                        color="primary"
                        onClick={() => {
                          handleReset();
                          submitForm();
                        }}
                      >
                        {t("employeesFilters.clearAll")}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={submitForm}
                      >
                        {t("employeesFilters.submit")}
                      </Button>
                    </ButtonGroup>
                  </Grid>
                </Grid>
              }
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmployeesFilters;
